import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { entitlementsApi, entitlementsQueryKeys } from "../entitlement/api";
import { usePlanScreens } from "../screens/usePlanScreens";

/**
 * Băng báo trạng thái gói, hiện trên mọi trang khi có chuyện phải nói (ADR-0053).
 *
 * <p>Trước đây hai trạng thái này chỉ hiện trong trang Ví: người phải biết mình đang chỉ-đọc lại
 * là lễ tân đang bấm "Tạo lịch hẹn" và nhận một lỗi, không phải người vừa mở trang Ví.
 *
 * <p>Băng chỉ nói điều đang THẬT SỰ xảy ra. Phòng khám chưa được bật chặn theo gói thì hết ân hạn
 * chưa khoá việc nhập liệu, nên câu "chỉ xem" không được hiện cho họ.
 *
 * <p>Dùng chung query với {@code useEntitlement}, nên không thêm lượt gọi nào.
 */
export function BillingStateBanner() {
  const { data } = useQuery({
    queryKey: entitlementsQueryKeys.mine(),
    queryFn: () => entitlementsApi.mine(),
    staleTime: 10 * 60 * 1000,
  });
  const plan = usePlanScreens();
  const state = data?.billingState;

  if (state === "GRACE") {
    return (
      <Banner tone="warning">
        <strong className="font-semibold">Gói dịch vụ đã hết hạn — đang trong 7 ngày ân hạn.</strong>{" "}
        Mọi chức năng vẫn hoạt động; chỉ tin nhắn SMS và Zalo tạm ngưng.
      </Banner>
    );
  }
  if (state === "READ_ONLY" && plan.enforced) {
    return (
      <Banner tone="danger">
        <strong className="font-semibold">Phòng khám đang ở chế độ chỉ xem.</strong> Dữ liệu vẫn xem
        và xuất được, nhưng chưa nhập liệu mới được; đặt lịch trực tuyến và đồng bộ với HIS ngoài
        đang tạm dừng. Không có dữ liệu nào bị xoá.
      </Banner>
    );
  }
  if (state === "READ_ONLY") {
    return (
      <Banner tone="warning">
        <strong className="font-semibold">Gói dịch vụ đã hết hạn.</strong> Chọn hoặc gia hạn gói để
        tiếp tục dùng đầy đủ.
      </Banner>
    );
  }
  return null;
}

function Banner({ tone, children }: { tone: "warning" | "danger"; children: React.ReactNode }) {
  const colors =
    tone === "danger"
      ? "border-destructive/40 bg-destructive/5 text-foreground"
      : "border-amber-300 bg-amber-50 text-amber-800";
  return (
    <div
      role="status"
      className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-2.5 text-sm ${colors}`}
    >
      <span>{children}</span>
      <Link
        to="/settings/subscription"
        className="shrink-0 rounded-md border border-current/30 bg-background px-3 py-1 font-medium hover:bg-muted"
      >
        Chọn hoặc gia hạn gói
      </Link>
    </div>
  );
}
